import { ServiceCatalogItem } from '../../services/serviceCatalogService';

/**
 * Spot vaccines among the treatments and medications typed into a SOAP plan.
 *
 * Why this exists: SOAP's Plan section has one list for treatments and
 * medications, and a vaccine given during a visit is written there like anything
 * else. But a vaccine is not a medication — it needs a Vaccination Event with a
 * lot, an expiry and a booster (spec §2, §3). Nothing was noticing, so the
 * vaccine stayed as text in the note and no record was ever created.
 *
 * Detection is driven by the clinic's OWN catalog — the `vaccination` category
 * of `ServiceCatalog` — not by a hard-coded list of vaccine names. A clinic that
 * renames its products, or stocks something unusual, keeps working; and a clinic
 * that has not set up a catalog simply gets no suggestions rather than wrong
 * ones. Matching is deliberately conservative: an exact name or alias match after
 * normalisation, never a fuzzy guess, because the cost of a false positive is
 * prompting a vet to create a clinical record that should not exist.
 */

export interface DetectedVaccine {
  /** The row's name exactly as the user typed it. */
  enteredName: string;
  /** The catalog product it matched. */
  catalogItem: ServiceCatalogItem;
}

/** Lowercase, collapse whitespace, drop surrounding punctuation. */
export function normaliseName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s ]+/g, ' ')
    .replace(/^[^a-z0-9]+|[^a-z0-9)]+$/g, '')
    .trim();
}

/**
 * Vets write the word "vaccine" and the catalog usually does not — a plan line
 * reads "West Nile Virus Vaccine" against a product called "West Nile Virus".
 * Stripping that one leading/trailing noise word is a fixed rule, not fuzzy
 * matching: it cannot turn one product into another, it only removes a word that
 * carries no identifying information in a catalog of vaccines.
 */
const NOISE = /^(?:vaccine|vaccination|vax)\b|\b(?:vaccine|vaccination|vax)$/g;

/** The keys a typed name may match on: as written, and with the noise word removed. */
export function matchKeys(raw: string): string[] {
  const base = normaliseName(raw);
  const stripped = normaliseName(base.replace(NOISE, ''));
  return stripped && stripped !== base ? [base, stripped] : [base];
}

export function detectVaccines(
  rows: { name?: string | null }[],
  catalog: ServiceCatalogItem[],
): DetectedVaccine[] {
  const vaccines = catalog.filter((c) => c.category === 'vaccination' && c.isActive);
  if (vaccines.length === 0) return [];

  // name/alias -> product. Built once per call; a clinic catalog is small.
  const index = new Map<string, ServiceCatalogItem>();
  vaccines.forEach((item) => {
    index.set(normaliseName(item.name), item);
    (item.aliases ?? []).forEach((alias) => {
      const key = normaliseName(alias);
      if (key && !index.has(key)) index.set(key, item);
    });
  });

  const seen = new Set<string>();
  const found: DetectedVaccine[] = [];

  rows.forEach((row) => {
    const raw = (row.name ?? '').trim();
    if (!raw) return;
    const hit = matchKeys(raw)
      .map((k) => index.get(k))
      .find(Boolean);
    if (!hit) return;
    // One suggestion per product, even if the vet listed it twice.
    if (seen.has(hit.id)) return;
    seen.add(hit.id);
    found.push({ enteredName: raw, catalogItem: hit });
  });

  return found;
}
